import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem as SelectMenuItem,
    TextField,
    Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TodayIcon from '@mui/icons-material/Today';
import { DateTime } from 'luxon';
import Axios from 'axios';
import { tokens } from '../../theme';
import getCookie from '../../utils/getCookies';
import AvailabilityModal from './AvailabilityModal';

const MEETING_TYPE_ICONS = {
    phone: <PhoneIcon fontSize="small" />,
    in_person: <LocationOnIcon fontSize="small" />,
};

const MEETING_TYPE_LABELS = {
    phone: 'Phone',
    in_person: 'In Person',
};

const STATUS_LABELS = {
    0: 'Upcoming',
    1: 'Completed',
    2: 'Canceled (Parent)',
    3: 'Canceled (Admin)',
    4: 'No Show',
};

const STATUS_COLORS = {
    0: '#e3f2fd',
    1: '#e8f5e9',
    2: '#fff3e0',
    3: '#ffebee',
    4: '#fce4ec',
};

export default function AppointmentAvailability() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [activeTab, setActiveTab] = useState(0);
    const [availabilities, setAvailabilities] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [overdue, setOverdue] = useState([]);
    const [stats, setStats] = useState({});
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingAvailability, setEditingAvailability] = useState(null);

    // Filter states for appointments
    const [statusFilter, setStatusFilter] = useState('');
    const [adminFilter, setAdminFilter] = useState('');
    const [dateRangeFilter, setDateRangeFilter] = useState({
        startDate: '',
        endDate: '',
    });

    const fetchData = async () => {
        setLoading(true);
        const token = getCookie('_auth');

        // Fetch each endpoint independently to handle partial failures
        try {
            const availRes = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/admin-availabilities`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAvailabilities(availRes.data.availabilities || []);
        } catch (error) {
            console.error('Error fetching availabilities:', error);
        }

        try {
            const appointRes = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/appointments`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAppointments(appointRes.data.appointments || []);
        } catch (error) {
            console.error('Error fetching appointments:', error);
        }

        try {
            const statsRes = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/appointments/stats/summary`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setStats(statsRes.data.stats || {});
        } catch (error) {
            console.error('Error fetching stats:', error);
        }

        try {
            const adminsRes = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/admin-availabilities/admins/list`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAdmins(adminsRes.data.admins || []);
        } catch (error) {
            console.error('Error fetching admins:', error);
        }

        try {
            const overdueRes = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/appointments/overdue`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setOverdue(overdueRes.data.overdue || []);
        } catch (error) {
            console.error('Error fetching overdue:', error);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreateAvailability = () => {
        setEditingAvailability(null);
        setModalOpen(true);
    };

    const handleEditAvailability = (availability) => {
        setEditingAvailability(availability);
        setModalOpen(true);
    };

    const handleToggleAvailability = async (id, currentlyActive) => {
        const action = currentlyActive ? 'deactivate' : 'activate';
        if (!window.confirm(`Are you sure you want to ${action} this availability?`)) return;

        const token = getCookie('_auth');
        try {
            await Axios.put(`${process.env.REACT_APP_URL}/api/v1/admin-availabilities/${id}/toggle-active`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchData();
        } catch (error) {
            console.error('Error toggling availability:', error);
            alert(error.response?.data?.error || 'Failed to toggle availability');
        }
    };

    const handleDeleteAvailability = async (id) => {
        if (!window.confirm('Are you sure you want to PERMANENTLY delete this availability? This action cannot be undone.')) return;

        const token = getCookie('_auth');
        try {
            await Axios.delete(`${process.env.REACT_APP_URL}/api/v1/admin-availabilities/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchData();
        } catch (error) {
            console.error('Error deleting availability:', error);
            alert(error.response?.data?.error || 'Failed to delete availability');
        }
    };

    const handleModalClose = (refreshData = false) => {
        setModalOpen(false);
        setEditingAvailability(null);
        if (refreshData) {
            fetchData();
        }
    };

    const handleUpdateAppointmentStatus = async (appointmentId, newStatus) => {
        // Confirmation messages for each status
        const confirmMessages = {
            1: 'Mark this appointment as completed?',
            3: 'Cancel this appointment? This action cannot be undone.',
            4: 'Mark this appointment as no-show?',
        };

        if (!window.confirm(confirmMessages[newStatus] || 'Update this appointment?')) {
            return;
        }

        const token = getCookie('_auth');
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/appointments/${appointmentId}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchData();
        } catch (error) {
            console.error('Error updating appointment status:', error);
            alert(error.response?.data?.error || 'Failed to update appointment status');
        }
    };

    // Filter appointments
    const filteredAppointments = useMemo(() => {
        return appointments.filter((appt) => {
            // Handle special filter values
            if (statusFilter === 'upcoming') {
                // Booked (0) and in the future
                if (appt.status !== 0) return false;
                const apptDate = DateTime.fromISO(appt.scheduled_at);
                if (apptDate < DateTime.now()) return false;
            } else if (statusFilter === 'canceled') {
                // Both parent (2) and admin (3) cancellations
                if (appt.status !== 2 && appt.status !== 3) return false;
            } else if (statusFilter !== '' && appt.status !== parseInt(statusFilter)) {
                return false;
            }
            if (adminFilter && appt.assigned_admin_id !== parseInt(adminFilter)) {
                return false;
            }
            if (dateRangeFilter.startDate || dateRangeFilter.endDate) {
                const apptDate = DateTime.fromISO(appt.scheduled_at);
                if (dateRangeFilter.startDate) {
                    const startDate = DateTime.fromISO(dateRangeFilter.startDate);
                    if (apptDate < startDate) return false;
                }
                if (dateRangeFilter.endDate) {
                    const endDate = DateTime.fromISO(dateRangeFilter.endDate).endOf('day');
                    if (apptDate > endDate) return false;
                }
            }
            return true;
        });
    }, [appointments, statusFilter, adminFilter, dateRangeFilter]);

    // Handle stat card clicks
    const handleStatClick = (statType) => {
        setActiveTab(1); // Switch to Appointments tab
        setAdminFilter(''); // Clear admin filter
        setDateRangeFilter({ startDate: '', endDate: '' }); // Clear date filter

        switch (statType) {
            case 'upcoming':
                setStatusFilter('upcoming');
                break;
            case 'completed':
                setStatusFilter('1');
                break;
            case 'canceled':
                setStatusFilter('canceled');
                break;
            case 'no_show':
                setStatusFilter('4');
                break;
            case 'total':
                setStatusFilter('');
                break;
            default:
                setStatusFilter('');
        }
    };

    const clearFilters = () => {
        setStatusFilter('');
        setAdminFilter('');
        setDateRangeFilter({ startDate: '', endDate: '' });
    };

    const filterToday = () => {
        const today = DateTime.now().setZone('America/New_York').toFormat('yyyy-MM-dd');
        setDateRangeFilter({ startDate: today, endDate: today });
    };

    const formatDaysOfWeek = (days) => {
        if (!days || days.length === 0) return '-';
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return days.map(d => dayNames[d]).join(', ');
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '-';
        const [hours, minutes] = timeStr.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f6fa', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h2" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>
                    Appointments
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreateAvailability}
                    sx={{
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            transition: 'transform 0.2s',
                        },
                    }}
                >
                    Add Availability
                </Button>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Box
                        onClick={() => handleStatClick('upcoming')}
                        sx={{
                            bgcolor: statusFilter === 'upcoming' ? colors.blueAccent[700] : 'white',
                            borderRadius: 2,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: statusFilter === 'upcoming' ? 3 : 1,
                            border: statusFilter === 'upcoming' ? `2px solid ${colors.blueAccent[500]}` : `1px solid ${colors.grey[300]}`,
                            cursor: 'pointer',
                            transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: 4 },
                        }}
                    >
                        <Avatar sx={{ bgcolor: colors.blueAccent[500], width: 40, height: 40, mb: 1 }}>
                            <EventAvailableIcon />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>{stats.upcoming || 0}</Typography>
                        <Typography variant="body2" sx={{ color: colors.primary[400] }}>Upcoming</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Box
                        onClick={() => handleStatClick('completed')}
                        sx={{
                            bgcolor: statusFilter === '1' ? colors.blueAccent[700] : 'white',
                            borderRadius: 2,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: statusFilter === '1' ? 3 : 1,
                            border: statusFilter === '1' ? `2px solid ${colors.blueAccent[500]}` : `1px solid ${colors.grey[300]}`,
                            cursor: 'pointer',
                            transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: 4 },
                        }}
                    >
                        <Avatar sx={{ bgcolor: colors.greenAccent[500], width: 40, height: 40, mb: 1 }}>
                            <CheckCircleIcon />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>{stats.completed || 0}</Typography>
                        <Typography variant="body2" sx={{ color: colors.primary[400] }}>Completed</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Box
                        onClick={() => handleStatClick('canceled')}
                        sx={{
                            bgcolor: statusFilter === 'canceled' ? colors.blueAccent[700] : 'white',
                            borderRadius: 2,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: statusFilter === 'canceled' ? 3 : 1,
                            border: statusFilter === 'canceled' ? `2px solid ${colors.blueAccent[500]}` : `1px solid ${colors.grey[300]}`,
                            cursor: 'pointer',
                            transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: 4 },
                        }}
                    >
                        <Avatar sx={{ bgcolor: colors.redAccent[400], width: 40, height: 40, mb: 1 }}>
                            <CancelIcon />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>{(stats.canceled_parent || 0) + (stats.canceled_admin || 0)}</Typography>
                        <Typography variant="body2" sx={{ color: colors.primary[400] }}>Canceled</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Box
                        onClick={() => handleStatClick('no_show')}
                        sx={{
                            bgcolor: statusFilter === '4' ? colors.blueAccent[700] : 'white',
                            borderRadius: 2,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: statusFilter === '4' ? 3 : 1,
                            border: statusFilter === '4' ? `2px solid ${colors.blueAccent[500]}` : `1px solid ${colors.grey[300]}`,
                            cursor: 'pointer',
                            transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: 4 },
                        }}
                    >
                        <Avatar sx={{ bgcolor: '#9e9e9e', width: 40, height: 40, mb: 1 }}>
                            <PersonIcon />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>{stats.no_show || 0}</Typography>
                        <Typography variant="body2" sx={{ color: colors.primary[400] }}>No Show</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Box
                        onClick={() => handleStatClick('total')}
                        sx={{
                            bgcolor: statusFilter === '' && activeTab === 1 ? colors.blueAccent[700] : 'white',
                            borderRadius: 2,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: statusFilter === '' && activeTab === 1 ? 3 : 1,
                            border: statusFilter === '' && activeTab === 1 ? `2px solid ${colors.blueAccent[500]}` : `1px solid ${colors.grey[300]}`,
                            cursor: 'pointer',
                            transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: 4 },
                        }}
                    >
                        <Avatar sx={{ bgcolor: colors.primary[400], width: 40, height: 40, mb: 1 }}>
                            <CalendarTodayIcon />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>{stats.total || 0}</Typography>
                        <Typography variant="body2" sx={{ color: colors.primary[400] }}>Total</Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ mb: 2 }}
            >
                <Tab label="Availabilities" />
                <Tab label="Appointments" />
                <Tab label={`Overdue (${overdue.length})`} />
            </Tabs>

            {/* Availabilities Tab */}
            {activeTab === 0 && (
                <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                    {/* Info message about overlapping availabilities */}
                    <Box sx={{ p: 2, borderBottom: `1px solid ${colors.grey[200]}`, display: 'flex', alignItems: 'flex-start', gap: 1, bgcolor: '#e3f2fd' }}>
                        <InfoOutlinedIcon sx={{ color: colors.blueAccent[500], mt: 0.3 }} fontSize="small" />
                        <Typography variant="body2" sx={{ color: colors.primary[400] }}>
                            <strong>Note:</strong> If multiple staff members have overlapping availability times, parents will see multiple time slots for the same time period.
                            Since staff names are not displayed to parents, they will simply choose from available slots without knowing which staff member is assigned.
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: colors.grey[100] }}>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Date(s)</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Time (ET)</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Days</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Duration</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Meeting Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Assigned To</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {availabilities.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: colors.grey[500] }}>
                                            No availabilities found. Click "Add Availability" to create one.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    availabilities.map((avail) => (
                                        <TableRow key={avail.id} hover>
                                            <TableCell>
                                                <Chip
                                                    label={avail.availability_type === 'one_time' ? 'One-time' : 'Recurring'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: avail.availability_type === 'one_time' ? '#e3f2fd' : '#f3e5f5',
                                                        color: colors.primary[400],
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: colors.primary[400] }}>
                                                {avail.availability_type === 'one_time'
                                                    ? DateTime.fromISO(avail.start_date).toFormat('MMM d, yyyy')
                                                    : `${DateTime.fromISO(avail.start_date).toFormat('MMM d, yyyy')} - ${avail.end_date ? DateTime.fromISO(avail.end_date).toFormat('MMM d, yyyy') : 'Ongoing'}`
                                                }
                                            </TableCell>
                                            <TableCell sx={{ color: colors.primary[400] }}>
                                                {formatTime(avail.start_time)} - {formatTime(avail.end_time)}
                                            </TableCell>
                                            <TableCell sx={{ color: colors.primary[400] }}>
                                                {avail.availability_type === 'recurring' ? formatDaysOfWeek(avail.days_of_week) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ color: colors.primary[400] }}>
                                                {avail.slot_duration} min
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {(avail.meeting_types || [avail.meeting_type]).map((type) => (
                                                        <Chip
                                                            key={type}
                                                            icon={MEETING_TYPE_ICONS[type]}
                                                            label={MEETING_TYPE_LABELS[type]}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#e3f2fd',
                                                                color: colors.primary[400],
                                                                '& .MuiChip-icon': { color: colors.primary[400] },
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ color: colors.primary[400] }}>
                                                {avail.assigned_admin
                                                    ? `${avail.assigned_admin.first_name} ${avail.assigned_admin.last_name}`
                                                    : avail.creator
                                                        ? `${avail.creator.first_name} ${avail.creator.last_name}`
                                                        : '-'
                                                }
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={avail.is_active ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: avail.is_active ? '#e8f5e9' : '#ffebee',
                                                        color: avail.is_active ? '#2e7d32' : '#c62828',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleEditAvailability(avail)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={avail.is_active ? "Deactivate" : "Activate"}>
                                                        <IconButton
                                                            size="small"
                                                            color={avail.is_active ? "warning" : "success"}
                                                            onClick={() => handleToggleAvailability(avail.id, avail.is_active)}
                                                        >
                                                            {avail.is_active ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Permanently Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteAvailability(avail.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Appointments Tab */}
            {activeTab === 1 && (
                <>
                    {/* Filters */}
                    <Box sx={{ mb: 2, bgcolor: '#f5f6fa', borderRadius: 2, p: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pl: 1 }}>
                            <FilterListIcon sx={{ color: colors.blueAccent[500] }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>
                                Filters
                            </Typography>
                        </Box>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={2.4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ color: colors.primary[400] }}>Status</InputLabel>
                                    <Select
                                        value={statusFilter}
                                        label="Status"
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        sx={{
                                            bgcolor: 'white',
                                            color: colors.primary[400],
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[300] },
                                        }}
                                    >
                                        <SelectMenuItem value="">All</SelectMenuItem>
                                        <SelectMenuItem value="upcoming">Upcoming</SelectMenuItem>
                                        <SelectMenuItem value="1">Completed</SelectMenuItem>
                                        <SelectMenuItem value="canceled">Canceled (All)</SelectMenuItem>
                                        <SelectMenuItem value="2">Canceled (Parent)</SelectMenuItem>
                                        <SelectMenuItem value="3">Canceled (Admin)</SelectMenuItem>
                                        <SelectMenuItem value="4">No Show</SelectMenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ color: colors.primary[400] }}>Assigned Admin</InputLabel>
                                    <Select
                                        value={adminFilter}
                                        label="Assigned Admin"
                                        onChange={(e) => setAdminFilter(e.target.value)}
                                        sx={{
                                            bgcolor: 'white',
                                            color: colors.primary[400],
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[300] },
                                        }}
                                    >
                                        <SelectMenuItem value="">All</SelectMenuItem>
                                        {admins.map((admin) => (
                                            <SelectMenuItem key={admin.id} value={admin.id}>
                                                {admin.first_name} {admin.last_name}
                                            </SelectMenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <TextField
                                    label="From Date"
                                    type="date"
                                    value={dateRangeFilter.startDate}
                                    onChange={(e) => setDateRangeFilter((prev) => ({ ...prev, startDate: e.target.value }))}
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        bgcolor: 'white',
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'white',
                                            '& fieldset': { borderColor: colors.grey[300] },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <TextField
                                    label="To Date"
                                    type="date"
                                    value={dateRangeFilter.endDate}
                                    onChange={(e) => setDateRangeFilter((prev) => ({ ...prev, endDate: e.target.value }))}
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        bgcolor: 'white',
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'white',
                                            '& fieldset': { borderColor: colors.grey[300] },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={filterToday}
                                        startIcon={<TodayIcon />}
                                        sx={{ bgcolor: colors.blueAccent[500], '&:hover': { bgcolor: colors.blueAccent[600] } }}
                                    >
                                        Today
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={clearFilters}
                                        sx={{ borderColor: colors.blueAccent[500], color: colors.blueAccent[500] }}
                                    >
                                        Clear
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Appointments Table */}
                    <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: colors.grey[100] }}>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Student</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Consultation #</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Parent</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Course</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Date & Time</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Meeting</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Assigned To</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAppointments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{ py: 4, color: colors.grey[500] }}>
                                                No appointments found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredAppointments.map((appt) => (
                                            <TableRow key={appt.id} hover>
                                                <TableCell sx={{ color: colors.primary[400] }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                        {appt.student_course?.student?.first_name} {appt.student_course?.student?.last_name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ color: colors.primary[400] }}>
                                                    {appt.student_course?.student?.consultation_number || '-'}
                                                </TableCell>
                                                <TableCell sx={{ color: colors.primary[400] }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                        <EmailIcon fontSize="small" sx={{ color: colors.grey[500], mt: 0.3 }} />
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {appt.student_course?.student?.parent_email || '-'}
                                                            </Typography>
                                                            {appt.student_course?.student?.parent_phone && (
                                                                <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                                                                    {appt.student_course?.student?.parent_phone}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: colors.primary[400] }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {appt.student_course?.course?.code}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                                                        {appt.student_course?.course?.course_title}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={appt.appointment_type === 'early' ? '1st' : '2nd'}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: appt.appointment_type === 'early' ? '#e8f5e9' : '#fff3e0',
                                                            color: colors.primary[400],
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ color: colors.primary[400] }}>
                                                    <Typography variant="body1">
                                                        {DateTime.fromISO(appt.scheduled_at).setZone('America/New_York').toFormat('MMM d, yyyy')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                                                        {DateTime.fromISO(appt.scheduled_at).setZone('America/New_York').toFormat('h:mm a')} ET
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {MEETING_TYPE_ICONS[appt.meeting_type]}
                                                        <Typography variant="body2" sx={{ color: colors.primary[400] }}>
                                                            {MEETING_TYPE_LABELS[appt.meeting_type]}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: colors.primary[400] }}>
                                                    {appt.assigned_admin
                                                        ? `${appt.assigned_admin.first_name} ${appt.assigned_admin.last_name}`
                                                        : '-'
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={STATUS_LABELS[appt.status]}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: STATUS_COLORS[appt.status],
                                                            color: colors.primary[400],
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {appt.status === 0 && (
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="Mark Completed">
                                                                <IconButton
                                                                    size="small"
                                                                    color="success"
                                                                    onClick={() => handleUpdateAppointmentStatus(appt.id, 1)}
                                                                >
                                                                    <CheckCircleIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Mark No Show">
                                                                <IconButton
                                                                    size="small"
                                                                    color="warning"
                                                                    onClick={() => handleUpdateAppointmentStatus(appt.id, 4)}
                                                                >
                                                                    <PersonIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Cancel">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleUpdateAppointmentStatus(appt.id, 3)}
                                                                >
                                                                    <CancelIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </>
            )}

            {/* Overdue Tab */}
            {activeTab === 2 && (
                <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: colors.grey[100] }}>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Student</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Consultation #</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>Course</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>1st Appointment</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: colors.primary[400] }}>2nd Appointment</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {overdue.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: colors.grey[500] }}>
                                            No overdue appointments found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    overdue.map((item) => (
                                        <TableRow key={item.student_course_id} hover>
                                            <TableCell sx={{ color: colors.primary[400] }}>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {item.student?.first_name} {item.student?.last_name}
                                                </Typography>
                                                {item.student?.korean_name && (
                                                    <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                                                        {item.student.korean_name}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: colors.primary[400] }}>
                                                {item.student?.consultation_number || '-'}
                                            </TableCell>
                                            <TableCell sx={{ color: colors.primary[400] }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {item.course?.code}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                                                    {item.course?.course_title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.early_status}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: item.early_status === 'Missed' ? '#ffebee' :
                                                                 item.early_status === 'Booked' ? '#e8f5e9' :
                                                                 '#f5f5f5',
                                                        color: item.early_status === 'Missed' ? '#c62828' :
                                                               item.early_status === 'Booked' ? '#2e7d32' :
                                                               '#757575',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.late_status}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: item.late_status === 'Missed' ? '#ffebee' :
                                                                 item.late_status === 'Booked' ? '#e8f5e9' :
                                                                 '#f5f5f5',
                                                        color: item.late_status === 'Missed' ? '#c62828' :
                                                               item.late_status === 'Booked' ? '#2e7d32' :
                                                               '#757575',
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Availability Modal */}
            <AvailabilityModal
                open={modalOpen}
                onClose={handleModalClose}
                availability={editingAvailability}
                admins={admins}
            />
        </Box>
    );
}
